import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Polyline, TileLayer, useMap } from 'react-leaflet';
import { Box, HairlineCard, palette } from '@munifocus/ui';
import { getTileConfig, NB_BOUNDS } from '../../lib/map-tiles';
import { OpenOnScope } from '../../components/map/OpenOnScope';

export interface OpsPin {
  id: string;
  lat: number;
  lng: number;
  /** Printed inside the pin: a stop number, or nothing. */
  label?: string;
  title: string;
  /** Drawn in the accent; others in the muted tone. */
  emphasis?: boolean;
}

function pinIcon(label: string, emphasis: boolean): L.DivIcon {
  const fill = emphasis ? palette.brand.accent : palette.semantic.closed;
  const text = label.replace(/[<>&"]/g, '');
  const svg = `
    <svg width="30" height="30" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="15" cy="15" r="13" fill="${fill}" stroke="${palette.background.paper}" stroke-width="2"/>
      <text x="15" y="19.5" text-anchor="middle" font-family="Manrope, sans-serif" font-size="12" font-weight="700" fill="${palette.background.paper}">${text}</text>
    </svg>`;
  return L.divIcon({ html: svg, className: 'ops-pin', iconSize: [30, 30], iconAnchor: [15, 15] });
}

function Fit({ points }: { points: Array<[number, number]> }) {
  const map = useMap();
  const key = points.map((p) => p.join(',')).join(';');
  const [fitted, setFitted] = useState('');
  useEffect(() => {
    if (!points.length || fitted === key) return;
    if (points.length === 1) map.setView(points[0] as [number, number], 14);
    else map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
    setFitted(key);
  }, [map, points, key, fitted]);
  return null;
}

/**
 * The small Leaflet map the operations screens share: numbered or plain pins,
 * and optional legs drawn between them. The list beside it carries the same
 * data for keyboard and screen-reader users; the map is a picture of it.
 */
export function OpsMap({
  pins,
  legs = [],
  start,
  onSelect,
  height = 320,
}: {
  pins: OpsPin[];
  legs?: Array<Array<{ lat: number; lng: number }>>;
  start?: { lat: number; lng: number } | null;
  onSelect?: (id: string) => void;
  height?: number;
}) {
  const tiles = getTileConfig();
  const points = useMemo(
    () => [
      ...pins.map((p) => [p.lat, p.lng] as [number, number]),
      ...(start ? [[start.lat, start.lng] as [number, number]] : []),
    ],
    [pins, start],
  );
  return (
    <HairlineCard sx={{ overflow: 'hidden', p: 0 }}>
      <Box
        sx={{
          height,
          '& .leaflet-container': {
            height: '100%',
            width: '100%',
            background: palette.background.parch,
          },
        }}
      >
        <MapContainer
          bounds={NB_BOUNDS}
          scrollWheelZoom={false}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer url={tiles.url} attribution={tiles.attribution} maxZoom={tiles.maxZoom} />
          <Fit points={points} />
          <OpenOnScope hasPoints={points.length > 0} />
          {legs.map((leg, i) => (
            <Polyline
              key={i}
              positions={leg.map((p) => [p.lat, p.lng] as [number, number])}
              pathOptions={{ color: palette.brand.accent, weight: 3, opacity: 0.8 }}
            />
          ))}
          {start && (
            <Marker
              position={[start.lat, start.lng]}
              icon={pinIcon('', true)}
              title=""
              opacity={0.6}
            />
          )}
          {pins.map((p) => (
            <Marker
              key={p.id}
              position={[p.lat, p.lng]}
              icon={pinIcon(p.label ?? '', p.emphasis ?? true)}
              title={p.title}
              alt={p.title}
              eventHandlers={onSelect ? { click: () => onSelect(p.id) } : undefined}
            />
          ))}
        </MapContainer>
      </Box>
    </HairlineCard>
  );
}
